import AddDocumentsSection from "@/components/Forms/AddDocumentsSection.tsx";
import AddNoteAddToCompanionSection from "@/components/Forms/AddNoteAddToCompanionSection.tsx";
import AddNoteNotificationSection from "@/components/Forms/AddNoteNotificationSection.tsx";
import { addNoteSchema } from "@/components/Forms/schema/note/addNoteSchema.ts";
import type { RhfDocumentsUploadRef } from "@/components/RhfDocumentsUpload.tsx";
import { useCaseTask } from "@/contexts/CaseTasksProvider.tsx";
import { useAddNoteToCase } from "@/mutations/useAddNoteToCase.tsx";
import { useFetchCaseByUUID } from "@/queries/useFetchCaseByUUID.ts";
import { useFetchEmployeeByType } from "@/queries/useFetchEmployeeByType.ts";
import type { CaseTask } from "@/types/caseTask.ts";
import type { DocumentData } from "@/types/document.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    MenuItem,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { RhfCheckbox, RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import {
    type BaseSyntheticEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export const tags = [
    "Accounting",
    "Client Contact",
    "Closing",
    "Disbursement",
    "File Activity / Review",
    "Intake",
    "Liability",
    "Loan",
    "MedPay",
    "Negotiation",
    "Providers",
    "Subrogation",
    "Termination Process",
    "UM/UIM",
];

export interface UserData {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
    team: string;
    initials: string;
}

export type AddNoteFormValues = z.infer<typeof addNoteSchema>;

type Props = {
    caseUUID: string;
};

const AddNoteDialog = ({ caseUUID }: Props): ReactNode => {
    const { caseTasksPending } = useCaseTask();
    const addNoteToCaseMutation = useAddNoteToCase(caseUUID);
    const [open, setOpen] = useState(false);
    const documentsUploadRef = useRef<RhfDocumentsUploadRef>(null);

    const {
        data: { data: caseData } = {},
    } = useFetchCaseByUUID({
        isQueryEnabled: caseUUID !== "",
        caseUUID: caseUUID,
    });

    const { data: employeesData } = useFetchEmployeeByType({
        type: "All",
        includeInactive: false,
    });

    const availableEmployees: UserData[] = useMemo(() => {
        if (!employeesData) {
            return [];
        }

        const employeeList = employeesData?.map((employee) => {
            return {
                id: employee.idUUID,
                firstName: employee.nameFirst ?? "",
                lastName: employee.nameLast ?? "",
                role: employee.dashboardVersion ?? "",
                team: employee.team ?? "",
                initials: `${employee.nameFirst?.substring(0, 1)}${employee.nameLast?.substring(
                    0,
                    1,
                )}`,
            };
        });

        return employeeList?.sort((a, b) => {
            if (a?.firstName && b?.firstName) {
                const firstNameComparison = a.firstName.localeCompare(b.firstName);

                if (firstNameComparison !== 0) {
                    return firstNameComparison;
                }
            }

            if (a?.lastName && b?.lastName) {
                return a.lastName.localeCompare(b.lastName);
            }

            return 0;
        });
    }, [employeesData]);

    const form = useForm<AddNoteFormValues>({
        resolver: zodResolver(addNoteSchema),
        defaultValues: {
            completeTask: false,
            enabled: false,
        },
    });

    const handleSubmit = useCallback(
        async (values: AddNoteFormValues) => {
            const newValues = { ...values };
            let uploadedDocuments: DocumentData[] = [];

            if (documentsUploadRef.current?.hasDocuments()) {
                uploadedDocuments = await documentsUploadRef.current.uploadDocuments(newValues.tag);
            }

            if (!newValues.addNoteToCompanions) {
                newValues.selectedCompanions = [];
            }

            addNoteToCaseMutation.mutate(
                {
                    ...newValues,
                    documents: uploadedDocuments,
                    caseTaskUUID: newValues.caseTaskUUID ?? null,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Note added.", { variant: "success" });
                        form.reset(form.getValues());
                        setOpen(false);
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Add note failed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        },
        [addNoteToCaseMutation, caseUUID],
    );
    const onSubmit: SubmitHandler<AddNoteFormValues> = async (
        data: AddNoteFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    useEffect(() => {
        if (open) {
            form.reset();
            const users: string[] = [];

            if (caseData) {
                if (caseData.assignedToAttorney) {
                    users.push(caseData.assignedToAttorney);
                }

                if (caseData.assignedToParalegal) {
                    users.push(caseData.assignedToParalegal);
                }
            }

            form.reset({
                completeTask: false,
                enabled: false,
                title: "",
                priority: "medium",
                notifyUsers: users,
                sendEmail: true,
            });
        }
    }, [caseData, open]);

    return (
        <>
            {!caseData?.id && <Alert>Error fetching Case ID</Alert>}
            <Button
                variant={"contained"}
                sx={{
                    ml: 2,
                    minWidth: "120px",
                }}
                onClick={() => {
                    form.reset();
                    setOpen(true);
                }}
            >
                New Note
            </Button>
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>New Note</DialogTitle>
                        <DialogContent dividers>
                            <Grid container>
                                <Grid size={12}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"tag"}
                                        label={"Tag"}
                                        sx={{
                                            width: "100%",
                                            mb: 2,
                                        }}
                                    >
                                        {tags.map((tag) => (
                                            <MenuItem key={tag} value={tag}>
                                                {tag}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                <Grid size={12}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"note"}
                                        label={"Note"}
                                        sx={{
                                            width: "100%",
                                            mb: 2,
                                        }}
                                        multiline
                                        rows={6}
                                    >
                                        {tags.map((tag) => (
                                            <MenuItem key={tag} value={tag}>
                                                {tag}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                            </Grid>
                            <Divider sx={{ mt: 1, mb: 2 }} />
                            <Grid container>
                                <Grid size={12}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"caseTaskUUID"}
                                        label={"Task (Optional)"}
                                        sx={{
                                            width: "100%",
                                            mb: 3,
                                        }}
                                    >
                                        {caseTasksPending.map((task: CaseTask) => (
                                            <MenuItem key={task.idUUID} value={task.idUUID}>
                                                {task.name}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                {form.watch("caseTaskUUID") && (
                                    <Grid size={12} sx={{ px: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <RhfCheckbox
                                                    control={form.control}
                                                    name="completeTask"
                                                />
                                            }
                                            label="Complete Task?"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <AddNoteNotificationSection availableUsers={availableEmployees} />
                            <AddNoteAddToCompanionSection caseUUID={caseUUID} />
                            <AddDocumentsSection
                                caseUUID={caseUUID}
                                documentsUploadRef={documentsUploadRef}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpen(false)}>Close</Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                Add Note
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default AddNoteDialog;
